import { StateCreator } from 'zustand';
import { getAgencyInsurances } from '../../services/api';
import type { AgencyInsuranceResponse } from '../../services/api/types.gen';

export interface AgencyInsuranceSlice {
  agencyInsurances: AgencyInsuranceResponse[];
  agencyInsurancesLoading: boolean;
  agencyInsurancesError: string | null;
  agencyInsurancesLastFetched: number | null;
  fetchAgencyInsurances: (
    agencyId?: string,
    agencyClinicId?: string
  ) => Promise<void>;
  shouldRefreshAgencyInsurances: () => boolean;
  clearAgencyInsurances: () => void;
  getAgencyInsuranceById: (id: string) => AgencyInsuranceResponse | undefined;
  getAgencyInsuranceByName: (
    name: string
  ) => AgencyInsuranceResponse | undefined;
}

export const agencyInsuranceSlice: StateCreator<AgencyInsuranceSlice> = (
  set,
  get
) => ({
  agencyInsurances: [],
  agencyInsurancesLoading: false,
  agencyInsurancesError: null,
  agencyInsurancesLastFetched: null,

  shouldRefreshAgencyInsurances: () => {
    const state = get();
    // Only refresh if we don't have any agency insurance data
    // Cache persists for the entire session (until page refresh)
    return state.agencyInsurances.length === 0;
  },

  fetchAgencyInsurances: async (agencyId?: string, agencyClinicId?: string) => {
    try {
      set({ agencyInsurancesLoading: true, agencyInsurancesError: null });
      const agencyInsurancesData = await getAgencyInsurances({
        query: {
          ...(agencyId ? { agency_id: agencyId } : {}),
          ...(agencyClinicId ? { agency_clinic_id: agencyClinicId } : {}),
        },
      });

      if (agencyInsurancesData && agencyInsurancesData.data) {
        set({
          agencyInsurances: agencyInsurancesData.data,
          agencyInsurancesLoading: false,
          agencyInsurancesError: null,
          agencyInsurancesLastFetched: Date.now(),
        });
      } else {
        set({
          agencyInsurancesLoading: false,
          agencyInsurancesError: 'No agency insurance data received',
        });
      }
    } catch (error) {
      console.error('Failed to fetch agency insurances:', error);
      set({
        agencyInsurancesLoading: false,
        agencyInsurancesError:
          'Failed to load insurance data. Please try again.',
      });
    }
  },

  clearAgencyInsurances: () => {
    set({
      agencyInsurances: [],
      agencyInsurancesLoading: false,
      agencyInsurancesError: null,
      agencyInsurancesLastFetched: null,
    });
  },

  getAgencyInsuranceById: (id: string) => {
    const state = get();
    return state.agencyInsurances.find(
      agencyInsurance => agencyInsurance.id === id
    );
  },

  getAgencyInsuranceByName: (name: string) => {
    const state = get();
    return state.agencyInsurances.find(agencyInsurance =>
      agencyInsurance.name.toLowerCase().includes(name.toLowerCase())
    );
  },
});
